import { deleteCardHook, getUserCards, updateCardHook } from "../api/api";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { HookType, HookConfig } from "../api/types";

/**
 * Handles updating a card hook by signing and sending the update request.
 *
 * @param chainId - Blockchain chain ID.
 * @param hookType - The type of hook to update (e.g., "yield_buy").
 * @param config - The configuration object for the hook.
 */
export const handleUpdateCardHook = async ({
  chainId,
  hookType,
  config,
}: {
  chainId: number;
  hookType: HookType;
  config: HookConfig<HookType>;
}): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    console.log("[handleUpdateCardHook] Starting hook update...");

    const { userCard, _setUserCard } = useCardStatusStore.getState();

    if (!userCard?.cardID) {
      console.warn("[handleUpdateCardHook] Wallet address or card ID missing.");
      return {
        success: false,
        message: "Wallet address or card ID is missing.",
      };
    }

    const result = await updateCardHook(
      userCard.cardID,
      chainId,
      hookType,
      config
    );

    if (result.success) {
      console.log("[handleUpdateCardHook] Hook updated successfully.");

      const userCardsResponse = await getUserCards();

      const initialUserCard = userCardsResponse.data?.[0] || null;
      _setUserCard(initialUserCard);

      return {
        success: true,
        message: "Card hook updated successfully.",
      };
    }

    return {
      success: false,
      message: result.message || "Failed to update card hook",
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[handleUpdateCardHook] Error:", err);
    return {
      success: false,
      message,
    };
  }
};

/**
 * Handles deleting a card hook by signing and sending the delete request.
 *
 * @return A promise that resolves to an object indicating success or failure,
 *         along with a message.
 */
export const handleDeleteCardHook = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    console.log("[handleDeleteCardHook] Starting hook delete...");

    const { userCard, _setUserCard } = useCardStatusStore.getState();

    if (!userCard?.cardID) {
      console.warn("[handleDeleteCardHook] Wallet address or card ID missing.");
      return {
        success: false,
        message: "Wallet address or card ID is missing.",
      };
    }

    const result = await deleteCardHook(userCard.cardID);

    if (result.success) {
      console.log("[handleDeleteCardHook] Hook deleted successfully.");

      const userCardsResponse = await getUserCards();

      const initialUserCard = userCardsResponse.data?.[0] || null;
      _setUserCard(initialUserCard);

      return {
        success: true,
        message: "Card hook deleted successfully.",
      };
    }

    return {
      success: false,
      message: result.message || "Failed to delete card hook",
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[handleDeleteCardHook] Error:", err);
    return {
      success: false,
      message,
    };
  }
};
